// Utility function to format timestamps consistently
export const formatTimestamp = (timestamp) => {
  // Ensure timestamp is treated as UTC by adding 'Z' suffix if not present
  const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const date = new Date(utcTimestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Get relative time string (e.g., '5 minutes ago', '2 weeks ago')
export const getRelativeTime = (timestamp, now = new Date()) => {
  const timestampDate = new Date(timestamp.endsWith('Z') ? timestamp : timestamp + 'Z');
  const diff = now - timestampDate;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (weeks < 4) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  return `${months} month${months > 1 ? 's' : ''} ago`;
};

// Format message for AI context (moved from main component)
export const formatMessageForAI = (message, now = new Date()) => {
  const timestamp = new Date(message.created_at.endsWith('Z') ? message.created_at : message.created_at + 'Z');
  const dayTime = timestamp.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).replace(',', '').replace(' at ', ' ');
  
  const relative = getRelativeTime(message.created_at, now);
  const roleName = message.role === 'assistant' ? 'Coach' : 'User';
  
  return `${roleName} (${dayTime}, ${relative}): "${message.content}"`;
};

// Parse and format message content with markdown-like syntax
export const parseMessageContent = (content) => {
  if (!content) return [];
  
  const lines = content.split('\n');
  const elements = [];
  let inList = false;
  let listItems = [];
  let listType = null; // 'bullet' or 'numbered'
  let inTrainingPlan = false;
  let trainingPlanItems = [];
  
  const processText = (text) => {
    // Process bold text (**text** or *text* for single asterisk)
    // Process italic text (_text_ or *text* when not used for bold)
    let processed = text
      // First handle double asterisks for bold
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      // Then handle single asterisks for italic (but not if they're part of double asterisks)
      .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em class="italic text-gray-300">$1</em>')
      // Handle underscores for italic
      .replace(/_([^_]+)_/g, '<em class="italic text-gray-300">$1</em>');
    
    return processed;
  };
  
  const finishList = () => {
    if (listItems.length > 0) {
      elements.push({
        type: 'list',
        listType,
        items: listItems
      });
      listItems = [];
      inList = false;
      listType = null;
    }
  };
  
  const finishTrainingPlan = () => {
    if (trainingPlanItems.length > 0) {
      elements.push({
        type: 'trainingPlan',
        items: trainingPlanItems
      });
      trainingPlanItems = [];
      inTrainingPlan = false;
    }
  };
  
  // Check if content looks like a training plan (multiple day entries)
  const hasMultipleDays = lines.some(line => 
    /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*\(/.test(line.trim())
  );
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (!line) {
      // Empty line - finish any current structures
      if (inList) {
        finishList();
      }
      if (inTrainingPlan && hasMultipleDays) {
        // Don't finish training plan on empty lines if we have multiple days
        // Just add spacing
        if (trainingPlanItems.length > 0) {
          elements.push({ type: 'break' });
        }
      }
      if (elements.length > 0 && !inTrainingPlan) {
        elements.push({ type: 'break' });
      }
      continue;
    }
    
    // Check for training plan format: "Monday (Nov 17) - Easy Run" or "Monday - Easy Run"
    if (hasMultipleDays && /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*\(?/.test(line)) {
      if (!inTrainingPlan) {
        finishList();
        inTrainingPlan = true;
      }
      
      // Parse the day line - can be "Monday (Nov 17) - Easy Run" or just "Monday (Nov 17)"
      const dayMatch = line.match(/^((?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*(?:\([^)]+\))?)\s*[-–—]\s*(.+)$/);
      if (dayMatch) {
        const [, dayPart, workoutPart] = dayMatch;
        trainingPlanItems.push({
          day: dayPart.trim(),
          workout: workoutPart.trim(),
          details: []
        });
      } else {
        // Just day name with optional date, workout/details will be on following lines
        const dayOnlyMatch = line.match(/^((?:Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*(?:\([^)]+\))?)\s*$/);
        if (dayOnlyMatch) {
          trainingPlanItems.push({
            day: dayOnlyMatch[1].trim(),
            workout: '',
            details: []
          });
        }
      }
      continue;
    }
    
    // If we're in a training plan and the previous item exists, add details
    if (inTrainingPlan && trainingPlanItems.length > 0 && 
        !/^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s*\(?/.test(line) &&
        !line.match(/^Total\s+weekly\s+volume:/i) &&
        !line.match(/^Here's your training plan/i) &&
        !line.match(/^This plan/i)) {
      const lastItem = trainingPlanItems[trainingPlanItems.length - 1];
      
      // If workout is empty and this line looks like a workout description (short, often has numbers/minutes)
      if (!lastItem.workout && (line.length < 80 || line.match(/\d+\s*(minutes?|mins?|hours?|hrs?)/i))) {
        lastItem.workout = line;
      } else {
        // This is a detail/description line
        lastItem.details.push(line);
      }
      continue;
    }
    
    // Check for "Total weekly volume:" which should finish training plan
    if (line.match(/^Total\s+weekly\s+volume:/i)) {
      if (inTrainingPlan) {
        finishTrainingPlan();
      }
      if (inList) {
        finishList();
      }
      elements.push({
        type: 'text',
        content: processText(line)
      });
      continue;
    }
    
    // Check for bullet points (•, -, *, +)
    const bulletMatch = line.match(/^[•\-*+]\s+(.+)$/);
    if (bulletMatch) {
      if (inTrainingPlan) {
        finishTrainingPlan();
      }
      if (!inList || listType !== 'bullet') {
        finishList();
        inList = true;
        listType = 'bullet';
      }
      listItems.push(processText(bulletMatch[1]));
      continue;
    }
    
    // Check for numbered lists (1., 2., etc.)
    const numberedMatch = line.match(/^\d+\.\s+(.+)$/);
    if (numberedMatch) {
      if (inTrainingPlan) {
        finishTrainingPlan();
      }
      if (!inList || listType !== 'numbered') {
        finishList();
        inList = true;
        listType = 'numbered';
      }
      listItems.push(processText(numberedMatch[1]));
      continue;
    }
    
    // Regular text - finish any current structures
    if (inTrainingPlan) {
      finishTrainingPlan();
    }
    if (inList) {
      finishList();
    }
    
    elements.push({
      type: 'text',
      content: processText(line)
    });
  }
  
  // Finish any remaining structures
  finishTrainingPlan();
  finishList();
  
  return elements;
}; 
/**
 * Copy text to clipboard with a `document.execCommand` fallback for non-secure contexts.
 * Returns a Promise<boolean> — true if the copy succeeded.
 */
export const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through to fallback */ }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (e) {
    return false;
  }
};
